export const aInput = () => {
  /**
   * Change the styling of form element depending on the state
   */
  const handleValidity = () => {
    const initClassName = 'initialized';

    const elements: HTMLElement[] = Array.from(
      document.querySelectorAll('[is="a-input"]')
    );

    /**
     * Check any form element for its validity and apply some classes so we can identify and modify the styles with CSS
     * @param element The [is="a-input"] element
     * @param input Any form element
     * @param onlyIfHasInitialValue Set to true when running checkElement before any changes were made to the {@link input}, for example on page load
     */
    const checkElement = (
      element: HTMLElement,
      input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
      onlyIfHasInitialValue?: boolean
    ) => {
      /** This element is valid on blur or not */
      const isValid =
        input.value &&
        !['on', 'off'].includes(input.value) &&
        input.checkValidity();
      const hasValue =
        input.value && !['on', 'off'].includes(input.value) ? true : false;
      const isInvalid =
        (input.value &&
          !['on', 'off'].includes(input.value) &&
          !input.checkValidity()) ||
        (input.required && !input.checkValidity());

      element.classList.toggle('hasValue', hasValue);

      if (onlyIfHasInitialValue !== true) {
        element.classList.toggle('isValid', isValid);
        element.classList.toggle('isInvalid', isInvalid);
      }
    };

    /**
     * Trigger form validity
     * @param {HTMLElement} formElement Any form element within a `<form>`
     */
    const triggerNativeFormValidation = (formElement) => {
      const name = formElement.name;
      if (!name) return;

      const form = Array.from(document.querySelectorAll('form')).find((form) =>
        form.querySelector(`[name="${name}"]`)
      );
      if (!form) return;

      if (!form.checkValidity()) {
        // Create a temporary submit button, click and remove it to trigger the native form validation
        const tmpSubmit = document.createElement('button');
        form.appendChild(tmpSubmit);
        tmpSubmit.click();
        form.removeChild(tmpSubmit);
      }
    };

    const init = () => {
      elements.forEach((element) => {
        const input:
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement =
          Array.from(element.querySelectorAll('input, textarea, select')).map(
            (x) => {
              if (x.nodeName === 'SELECT') return <HTMLSelectElement>x;
              if (x.nodeName === 'TEXTAREA') return <HTMLTextAreaElement>x;
              return <HTMLInputElement>x;
            }
          )[0];

        element.classList.add(initClassName);

        input.addEventListener('blur', () => {
          checkElement(element, input);
        });
        input.addEventListener('change', () => {
          checkElement(element, input);
          triggerNativeFormValidation(input);
        });
        input.addEventListener('focus', () => {
          checkElement(element, input);
        });
      });
    };

    const onKeydown = () => {
      init();
    };

    document.addEventListener('keydown', onKeydown);

    elements.forEach((element) => {
      const input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement =
        Array.from(element.querySelectorAll('input, textarea, select')).map(
          (x) => {
            if (x.nodeName === 'SELECT') return <HTMLSelectElement>x;
            if (x.nodeName === 'TEXTAREA') return <HTMLTextAreaElement>x;
            return <HTMLInputElement>x;
          }
        )[0];

      checkElement(element, input, true);
    });
  };

  /**
   * Add icons to <select> element
   */
  const handleSelect = () => {
    const elements: HTMLElement[] = Array.from(
      document.querySelectorAll('[is="a-input"][data-type="select"]')
    );

    const iconHtml = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none"><path fill="currentColor" fill-rule="evenodd" d="m12.665 13.693 4.224-4.224a.455.455 0 1 1 .642.642L13.2 14.443a.757.757 0 0 1-1.072 0l-4.33-4.332a.455.455 0 1 1 .643-.642l4.224 4.224Z" clip-rule="evenodd"/></svg>`;

    elements.forEach((element) => {
      const inputWrapper: HTMLElement = element.querySelector('.inputWrapper');
      if (!inputWrapper) return;

      const icon = document.createElement('span');
      icon.classList.add('iconSelect');
      icon.innerHTML = iconHtml;
      inputWrapper.append(icon);
      icon.setAttribute('aria-hidden', 'true');
    });
  };

  const handleDisabled = () => {
    const elements: HTMLElement[] = Array.from(
      document.querySelectorAll('[is="a-input"]')
    );

    const checkDisabled = (
      element: HTMLElement,
      input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    ) => {
      element.classList.toggle('isDisabled', input.disabled);
    };

    elements.forEach((element) => {
      const input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement =
        Array.from(element.querySelectorAll('input, textarea, select')).map(
          (x) => {
            if (x.nodeName === 'SELECT') return <HTMLSelectElement>x;
            if (x.nodeName === 'TEXTAREA') return <HTMLTextAreaElement>x;
            return <HTMLInputElement>x;
          }
        )[0];

      checkDisabled(element, input);

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'disabled') {
            checkDisabled(element, input);
          }
        });
      });

      observer.observe(input, { attributes: true });
    });
  };

  /**
   * Input elements can be activated/deactivated with specific checkboxes ({@link toggles}) that have a dataset attribute: `[data-toggle]`.
   */
  const handleToggle = () => {
    /**
     * Elements that control other elements
     */
    const toggles: HTMLInputElement[] = Array.from(
      document.querySelectorAll(
        '[is="a-input"] input[type="checkbox"][data-toggle]'
      )
    );

    toggles.forEach((toggle) => {
      /** Element that is controlled by the {@link toggle} */
      const reference: HTMLElement = document.getElementById(
        toggle.dataset.toggle
      );

      if (!reference) return;

      /**
       * Form elements in the {@link formElements}
       */
      const formElements: (
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLDataListElement
        | HTMLTextAreaElement
      )[] = Array.from(
        reference.querySelectorAll('input, textarea, select, datalist')
      );

      /**
       * Show the {@link reference} and enable all {@link formElements}
       */
      const showHiddenField = () => {
        reference.inert = false;
        formElements?.forEach((inputInRefElement) => {
          inputInRefElement.removeAttribute('disabled');
        });
      };

      /**
       * Hide the {@link reference} and disable all {@link formElements}
       */
      const hideHiddenField = () => {
        reference.inert = true;
        formElements?.forEach((inputInRefElement) => {
          inputInRefElement.setAttribute('disabled', 'true');
        });
      };

      /**
       * Change the visible/hidden state on click
       */
      const onToggle = () => {
        if (toggle.checked) {
          showHiddenField();
        } else {
          hideHiddenField();
        }
      };

      toggle.addEventListener('change', onToggle);
      hideHiddenField();
    });
  };

  handleValidity();
  handleSelect();
  handleDisabled();
  handleToggle();
};
